{
    "redo": "Redo Onboarding",
    "title": "Meet Cephable",
    "description": "Meet Cephable, your personal AI assistant and share your interest for personalized use cases.",
    "congrats": "Congrats!",
    "overview": {
        "title": "Overview",
        "description": "Cephable is your personal AI — built for fast actions, voice commands, content generation, and smart automation across your tools. Out-of-the-box controls. No cloud processing. Just productivity that moves with you."
    },
    "customControls": {
        "tourLabel": "Always Available Global & Device Controls",
        "rowDescription": "Global and device controls are always available if your microphone is on, no matter what app you are using. You can use these controls to interact with your device without having to create new app controls. They contain helpful commands like swapping controls or apps, generating text, pausing your microphone and more."
    },
    "profile": {
        "tourLabel": "App Controls",
        "learn": "Learn how to set up your app controls and preferences in Cephable.",
        "rowDescription": "App controls are configured sets of controls that you can use to make things happen. Want to say 'Next Slide' to go to the next slide in your presentation? Or use head movement to move the cursor and voice controls for editing shortcuts? You can do that with app controls. The controls row shows you all the controls available for your current app.",
        "check": {
            "title": "Check your available controls.",
            "description": "App controls are available for each app you use. Here you can see the controls that are available for the app you are currently using. Try another command to see how it works. BUT, make sure you are focused on the app you want to control."
        }
    },
    "hotkeys": {
        "tourLabel": "Quick Action Buttons",
        "learn": "Learn how to set up quick action buttons to map multiple actions to a single button.",
        "rowDescription": "Quick action buttons allow you to perform multiple actions with a single command. For example, you can create a button that opens your favorite music app and starts playing a specific playlist.",
        "check": {
            "title": "Check your available quick actions.",
            "description": "Quick action buttons are available for each app you use. Here you can see the buttons that are available for the app you are currently using. Try another command to see how it works. BUT, make sure you are focused on the app you want to control."
        }
    },
    "dictation": {
        "tourLabel": "Dictation",
        "learn": "Learn how to use dictation mode to convert your speech to text hands-free.",
        "welcome": {
            "title": "Dictation",
            "description": "Dictation lets you speak naturally and have your words typed while you talk. It's like having a personal assistant who types everything you say."
        },
        "microphone": {
            "title": "Turn on Your Microphone",
            "description": "First, turn on your microphone. Click the Microphone Toggle on the Cephable Panel."
        },
        "activate": {
            "title": "Start Dictation Mode",
            "description": "Voice starts in command mode. To start dictation, say \"Start dictation\" or click the Dictation button. Lets turn on dictation mode now."
        },
        "focus": {
            "title": "Focus on a Text Field or Document",
            "description": "Dictation works best when you focus on a text field or document. Click on a text field or document where you want to dictate."
        },
        "speaking": {
            "title": "Start Speaking",
            "description": "Now you can start speaking. Everything you say will be typed in the text field or document you focused on. Try saying something like \"Hello, this is a test of dictation mode.\""
        },
        "pauseResume": {
            "title": "Pausing Dictation",
            "description": "Need a break without turning off dictation? Just say \"Stop Listening\". You can resume anytime with \"Start dictation.\" again. You can customize this further with your always on global and device controls."
        },
        "stop": {
            "title": "Stopping Dictation",
            "description": "Need to switch back to your app controls?  Just say \"Stop dictation\". This will stop dictation mode and return you to command mode. You can resume anytime with \"Start dictation.\" again."
        },
        "complete": {
            "description": "That's it! You can now dictate, pause, resume, and swap between dictation and command modes. Dictation is a powerful tool for hands-free typing. Use it to write emails, documents, or anything else you need to type out loud."
        }
    },
    "replyToSocialMedia": {
        "tourLabel": "Faster Social Media Replies with Dictation",
        "learn": "Learn how to use dictation to reply to social media posts hands-free.",
        "navigateTo": {
            "title": "Navigate to a Social Media Post",
            "description": "Open your web browser and navigate to a social media platform like LinkedIn or Facebook. Find a post you want to reply to."
        },
        "speaking": {
            "title": "Start Speaking",
            "description": "Now you can start speaking. Everything you say will be typed in the text field or document you focused on. Try saying something like \"Congrats! I am excited for you.\" Make sure your cursor is focused on a text field or comment box."
        },
        "complete": {
            "description": "That's it! You can now use dictate, pause, resume, and swap between dictation and command modes to reply to social media posts hands-free. Dictation is a powerful tool for hands-free typing. Use it to write emails, documents, or anything else you need to type out loud."
        }
    },
    "controllingBackgroundMusic": {
        "tourLabel": "Manage Background Music without Interrupting Your Flow",
        "learn": "Learn how to use voice controls and quick actions to control your music hands-free, while staying focused on your current task.",
        "tryOpen": {
            "title": "Try saying `Open Spotify` or `Open Apple Music`"
        },
        "play": {
            "title": "Try saying `Play` to start playing music",
            "description": "Make sure your microphone is on and say `Play`. This will start playing music in your chosen music app. This also works for any other media app."
        },
        "newPlaylist": {
            "title": "Create a New Playlist",
            "description": "Make sure your microphone is on and say `New Playlist`. This will start the process of creating a new playlist in your chosen music app."
        },
        "finishedCommands": {
            "title": "Great job! You’ve learned how to control your music hands-free.",
            "description": "You now know how to use voice commands and quick actions to play, pause, skip tracks, and adjust volume in your favorite music apps. Try exploring more commands or customizing your own quick actions for even smoother music control."
        }
    },
    "editingVideos": {
        "tourLabel": "Focus on the Story, Not the Tedious Clicks for Video Editing",
        "learn": "Learn how to use voice controls and quick actions to edit videos faster, and remove the cognitive load of remembering which buttons to click while editing.",
        "tryOpen": {
            "title": "Try saying `Open iMovie` or `Open DaVinci Resolve`"
        },
        "play": {
            "title": "Try saying `Play` to start playing your video",
            "description": "Make sure your microphone is on and say `Play`. This will start playing your video in your chosen video editing app."
        },
        "stop": {
            "title": "Stop Video Playback",
            "description": "Make sure your microphone is on and say `Stop`. This will stop video playback in your chosen video editing app."
        },
        "finishedCommands": {
            "title": "Great job! You’ve learned how to control your video editing hands-free.",
            "description": "You now know how to use voice commands and quick actions to play, pause, skip tracks, and adjust volume in your favorite video editing apps. Try exploring more commands or customizing your own quick actions for even smoother video control."
        }
    },
    "fpsGamePlay": {
        "tourLabel": "Play FPS Games with Voice Controls",
        "learn": "Learn how to use voice controls and quick actions to play FPS games.",
        "changeProfile": {
            "title": "Change Control Profile",
            "description": "Change your app control to the FPS game profile. You can customize this for the specific game you want to play with too. Refer to the video."
        },
        "autoProfileSwitching": {
            "title": "Turn Off Auto Control Switching",
            "description": "This will keep your current app control active until you change apps."
        },
        "tryOpen": {
            "title": "Try saying `Open Valorant` or `Open Battlefield`"
        },
        "go": {
            "title": "Try saying `Go`",
            "description": "Make sure your microphone is on and say `Go`. This will start moving your character forward."
        },
        "stop": {
            "title": "Try saying `Stop`",
            "description": "Make sure your microphone is on and say `Stop`. This will stop moving your character."
        },
        "complete": {
            "description": "You now know how to use voice commands and quick actions to move, stop, and control your character in FPS games. Try exploring more commands or customizing your own quick actions for even smoother and faster gameplay."
        }
    },
    "vibeCoding": {
        "tourLabel": "Ideate, Prototype, and Iterate Faster with Dictation",
        "learn": "Learn how to use dictation and AI-powered text generation to vibe code, prototype, and iterate on ideas.",
        "navigateTo": {
            "title": "Navigate to a Vibe Code Editor",
            "description": "Open your web browser and navigate to a Vibe Code Editor. Some examples are Vercel V0, GitHub Copilot in VS Code, Base 44, and Lovable."
        },
        "focus": {
            "title": "Click New Chat or Focus a Text Box",
            "description": "Dictation works best when you focus on a text field or document. Click on the comment box or reply thread where you want to dictate. Then start speaking."
        },
        "speaking": {
            "title": "Start Speaking",
            "description": "Now you can start speaking. Everything you say will be typed in the text field or document you focused on. Try saying your technical requirements and what the code should do. Make sure your cursor is focused on a text field or comment box."
        },
        "rewriteIntro": {
            "title": "Rewrite Code with Instructions",
            "description": "You can also use the Intelligent Tasks like Rewrite to modify existing code in place. This task is powered by on-device AI meaning it works without internet connection and runs from your device."
        },
        "highlightText": {
            "title": "Highlight Text to Rewrite",
            "description": "Highlight the code you want to rewrite."
        },
        "enterInstructions": {
            "title": "Enter Rewrite Instructions",
            "description": "Type your coding rewrite instructions. Specify the programming language, style, or any specific changes you want."
        },
        "complete": {
            "description": "That's it! You can now dictate, pause, resume, and iterate on Code. Continue exploring more use cases with Dictation and Intelligent Tasks, or other features to optimize your workflow."
        }
    },
    "documentCode": {
        "tourLabel": "Fast, Secure Code Documentation with On-Device AI Text Generation",
        "learn": "Learn how to use AI-powered rewrite to document code while staying in the same app and keeping your data secure.",
        "rewriteIntro": {
            "title": "Write Code Documentation",
            "description": "You can use Cephable Intelligent Rewrite to write code documentation in a separate document or app. Rewrite is part of On-device AI Tasks including Offline Generate and Translate."
        },
        "focusApp": {
            "title": "Focus on your Code",
            "description": "Open your IDE, and focus on the code you want to document."
        },
        "newFile": {
            "title": "Create a Documentation File",
            "description": "Create a new file in your IDE or open a separate app where you want the documentation to appear."
        },
        "highlightText": {
            "title": "Highlight Code",
            "description": "Highlight and copy the code you want to document."
        },
        "pasteMode": {
            "title": "Click Paste Text Tab",
            "description": "Use paste mode to paste the original code and write documentation in a new file."
        },
        "pasteInput": {
            "title": "Paste Code to Document",
            "description": "Paste the code you want to document into the input box."
        },
        "enterInstructions": {
            "title": "Enter Formatting, Style, and Content Instructions",
            "description": "Type your coding documentation instructions. Specify the file type (ie markdown), style, or any specific content you want."
        },
        "complete": {
            "description": "You have learned how to use Intelligent Tasks for quick and secure code documentation. Continue exploring more use cases with Rewrite, or other features to optimize your workflow."
        }
    },
    "generateCode": {
        "tourLabel": "Fast, Secure Code Generation with On-Device AI Text Generation",
        "learn": "Learn how to use AI-powered generation to create code snippets while moving across apps and keeping your data secure.",
        "rewriteIntro": {
            "title": "Write Code Snippets",
            "description": "You can use Cephable Intelligent Rewrite to write code snippets in your IDE while getting requirements from a different application. Rewrite is part of On-device AI Tasks including Offline Generate and Translate."
        },
        "focusApp": {
            "title": "Focus on your Ticket Contents",
            "description": "Open your ticket that specifies requirements, and focus on the content you want to generate code for."
        },
        "newFile": {
            "title": "Create a Code Snippet File",
            "description": "Create a new file in your IDE."
        },
        "highlightText": {
            "title": "Highlight Ticket Contents",
            "description": "Highlight and copy the content you want to generate code for."
        },
        "pasteMode": {
            "title": "Click Paste Text Tab",
            "description": "Use paste mode to paste the original ticket requirements and write code snippets for."
        },
        "pasteInput": {
            "title": "Paste Requirements to Generate Code",
            "description": "Paste the requirements you want to use for code generation into the input box."
        },
        "enterInstructions": {
            "title": "Enter Programming Language and Style Instructions",
            "description": "Type your programming language and style instructions. Specify the programming language (ie c#), style, or any specific coding instructions you have."
        },
        "translateIntro": {
            "title": "Translate Code Snippets",
            "description": "You can also use Cephable Intelligent Translate to convert code snippets from one programming language to another directly in your IDE. Translate is part of On-device AI Tasks including Offline Generate and Rewrite."
        },
        "copyGeneratedCode": {
            "title": "Copy the Generated Code",
            "description": "After generating code using the previous steps, highlight and copy the generated code snippet."
        },
        "translatePasteMode": {
            "title": "Click Paste Text Tab",
            "description": "Use paste mode to paste the original code and translate it to another programming language."
        },
        "enterProgrammingLanguage": {
            "title": "Enter Target Programming Language",
            "description": "Type the programming language you want to translate the code into (ie Python)."
        },
        "pasteCode": {
            "title": "Paste the Generated Code in your IDE",
            "description": "Go to your IDE and paste the copied code snippet into the new file you created earlier."
        },
        "complete": {
            "description": "You have learned how to use Intelligent Tasks for quick and secure code generation and translation. Continue exploring more use cases with Generate and Translate, or other features to optimize your workflow."
        }
    },
    "requirementDocsFromMeeting": {
        "tourLabel": "From Notes to Product Requirement Docs in Minutes using On-Device AI",
        "learn": "Learn how to use AI Intelligent Tasks to create structured product requirement documents from meeting notes while keeping your data secure.",
        "rewriteIntro": {
            "title": "Create Product Requirement Documents",
            "description": "You can use Cephable Intelligent Rewrite to create structured product requirement documents from meeting notes. Rewrite is part of On-device AI Tasks including Offline Generate and Translate."
        },
        "focusNotes": {
            "title": "Open your Meeting Notes",
            "description": "Open your meeting notes, transcriptions, or other meeting related text content."
        },
        "highlight": {
            "title": "Highlight Meeting Notes",
            "description": "Highlight the meeting notes you want to use to create a product requirement document."
        },
        "enterInstructions": {
            "title": "Enter Document Structure and Content Instructions",
            "description": "Type your product requirement document instructions. Specify the structure, style, or any specific content you want."
        },
        "complete": {
            "description": "You have learned how to use Intelligent Tasks for quick and secure product requirement document creation. Continue exploring more use cases with Rewrite, or other features to optimize your workflow."
        }
    },
    "headMouseMovement": {
        "tourLabel": "Hands-Free Gaming Setup with Head Mouse",
        "learn": "Learn how to use head movements to control your mouse cursor for precise navigation and clicking.",
        "deviceSettings": {
            "title": "Open Device Settings",
            "description": "To enable head mouse movements, click the Device Settings button."
        },
        "cameraSettings": {
            "title": "Open Camera Settings",
            "description": "In the Device Settings, click on Camera Settings to access the camera controls."
        },
        "headMouseSection": {
            "title": "Head Movement for Mouse Controls",
            "description": "When this is turned on, then some of your head movement controls will be used to move your mouse instead of trigger controls."
        },
        "steadyGlide": {
            "title": "Steady Glide Mode",
            "description": "Turn on Steady Glide mode. When you move your head past a certain point, the cursor glides continuously in that direction at a steady speed."
        },
        "customization": {
            "title": "Customize Head Mouse Settings",
            "description": "You can customize the head mouse settings to fit your preferences. Adjust the speed, sensitivity, and other settings to get the best experience."
        },
        "save": {
            "title": "Save Head Mouse Settings",
            "description": "Don't forget to save your customized head mouse settings."
        },
        "transition": {
            "title": "Moving to the Cephable Panel",
            "description": "Now let's move to the Cephable Panel to try out head mouse movements."
        },
        "playGames": {
            "title": "Play Games with Head Mouse",
            "description": "Now you can use head movements to control your mouse cursor while playing games. Try it out and see how it works!"
        }
    },
    "clinicalNoteTaking": {
        "tourLabel": "HIPAA-Compliant Dictation and Formatting for Clinicians",
        "learn": "Learn how to use voice commands, smart dictation, and AI formatting to save time on clinical notes.",
        "focus": {
            "title": "Focus on a Document",
            "description": "Dictation works best when you focus on a document. Open a Document where you want to take clinical notes between sessions."
        },
        "speaking": {
            "title": "Start Speaking",
            "description": "Now you can start speaking. Everything you say will be typed in the text field or document you focused on. Try saying something like \"Patient is a 45-year-old male with a history of hypertension. The last medication prescribed did not work as expected and caused an allergic reaction. We will need to check for alternative medicine, and they would like a Generic branded prescription.\" Make sure your cursor is focused on the document."
        },
        "rewriteIntro": {
            "title": "Format Your Notes",
            "description": "Now, you can use Cephable Intelligent Rewrite to format your notes in SOAP style. This is all done on-device, meaning your data stays private, secure, and HIPAA-compliant."
        },
        "copyText": {
            "title": "Copy the Notes",
            "description": "After highlighting your notes, copy the text to prepare it for formatting."
        },
        "pasteMode": {
            "title": "Click Paste Text Tab",
            "description": "Use paste mode to paste the original notes and format them in SOAP style."
        },
        "pasteInput": {
            "title": "Paste Notes to Format",
            "description": "Paste the notes you want to format into the input box."
        },
        "enterInstructions": {
            "title": "Enter Formatting Instructions",
            "description": "You can write formatting instructions that will need be followed for your Clinic / EHR System. (Ex. Format in SOAP style with headings, bullet points, and concise language.)"
        },
        "navigateToEHR": {
            "title": "Navigate to your EHR System",
            "description": "Open your EHR system and navigate to the section where you want to input the formatted notes."
        },
        "focusTextField": {
            "title": "Focus on the Text Field",
            "description": "Click on the text field where you want to paste the formatted notes."
        },
        "complete": {
            "description": "You have learned how to use voice commands and smart dictation to document clinical notes, and Intelligent Rewrite to format them in SOAP style. Continue exploring more use cases with Dictation and Rewrite, or other features to optimize your workflow."
        }
    },
    "noteTakingAndDocumentation": {
        "tourLabel": "Note Taking and Document Generation using Private, On-Device AI",
        "learn": "Learn how to use voice commands, smart dictation, and AI Tasks to go from notes to designs and documents faster.",
        "focus": {
            "title": "Focus on a Text Field or Document",
            "description": "Dictation works best when you focus on a text field or document. Open a Document or Click a Text Field where you want to take notes."
        },
        "speaking": {
            "title": "Start Speaking",
            "description": "Now you can start speaking. As you think through your idea, just talk naturally and let your thoughts flow. Everything you say will be typed in the text field or document you focused on. Try describing your concept, outlining steps, or brainstorming freely. For example, say \"I'm considering a new workflow for our team. First, we should automate the reporting process, then review the results weekly.\" Make sure your cursor is focused on the document."
        },
        "typeEnter": {
            "title": "Paragraph Breaks",
            "description": "To create paragraph breaks, simply press Enter on your keyboard. This will help organize your notes into clear sections. Continue speaking your thoughts, or transcribing from someone else talking."
        },
        "rewriteIntro": {
            "title": "Create Designs",
            "description": "Now, you can use Cephable Intelligent Rewrite to take your notes and design it into a feature idea or follow ups. This is all done on-device, meaning your data stays private and secure."
        },
        "highlightText": {
            "title": "Highlight the Notes",
            "description": "Select all the notes you want to use to create a design or document."
        },
        "inPlaceReplacement": {
            "title": "In Place Ideation",
            "description": "Now, we will use in-place replacement to turn your notes into a design or document. You can always use the paste mode or option to create a new file instead."
        },
        "enterInstructions": {
            "title": "Enter Design Instructions",
            "description": "You can write your next steps instruction of what you would like to do with the notes. This could be formatting, designing, follow up tasks, or anything else. (Ex. Create a design document highlighting how the feature would work, what the end to end looks like, and what success might look like.)"
        },
        "complete": {
            "description": "You have learned how to use voice commands and smart dictation to document clinical notes, and Intelligent Rewrite to format them in SOAP style. Continue exploring more use cases with Dictation and Rewrite, or other features to optimize your workflow."
        }
    },
    "webBrowsing": {
        "tourLabel": "Plan Trips, Shop Online, and Browse the Web using Voice",
        "learn": "Learn how to use voice commands to navigate, scroll, and fill forms while browsing the web hands-free.",
        "tryOpen": {
            "title": "Try saying `Open Chrome` or `Open Edge`",
            "description": "Make sure your microphone is on and say `Open Chrome` or `Open Edge`. This will open your chosen web browser. If you have automatic control switching enabled, it will also switch to the app controls that best match the app you are using."
        },
        "search": {
            "title": "Searching",
            "description": "Try saying \"Search\" or \"Search for\". This will focus the search bar in your browser."
        },
        "quickType": {
            "title": "Quick Typing",
            "description": "Quick Typing allows you to type using your voice without being in dictation mode. Try saying \"Type what is the weather today\". This will type out your query in the search bar."
        },
        "enter": {
            "title": "Press Enter",
            "description": "Try saying \"Enter\". This will execute the search query you just typed."
        },
        "scrollDown": {
            "title": "Scroll Down",
            "description": "Try saying \"Scroll down\". This will scroll down the webpage."
        },
        "scrollUp": {
            "title": "Scroll Up",
            "description": "Try saying \"Scroll up\". This will scroll up the webpage."
        },
        "newTab": {
            "title": "Open a New Tab",
            "description": "Try saying \"New tab\". This will open a new tab in your browser."
        },
        "formsCustomization": {
            "title": "Form Filling Customization",
            "description": "You can customize this app control for easy form filling. Like first name, last name, email etc. Then you can use Quick Actions or Voice Commands to fill out forms even faster."
        },
        "complete": {
            "title": "Great job!",
            "description": "You now know how to use voice commands and quick actions to navigate, search, scroll, and fill forms while browsing the web. Try exploring more commands or customizing your own quick actions for even smoother web browsing."
        }
    },
    "spreadsheetReviews": {
        "tourLabel": "Work Faster in Spreadsheets with Voice Controls",
        "learn": "Learn how to use voice commands and quick actions to review and edit large spreadsheets quickly.",
        "tryOpen": {
            "title": "Try saying `Open Excel` or `Open Google Sheets`",
            "description": "Make sure your microphone is on and say `Open Excel` or `Open Google Sheets`. This will open your chosen spreadsheet app. If you have automatic control switching enabled, it will also switch to the app controls that best match the app you are using."
        },
        "selectRow": {
            "title": "Select a Row",
            "description": "Click a cell. Then, try saying \"Select row\". This will select the entire row for the focused cell."
        },
        "delete": {
            "title": "Delete Contents of a Row",
            "description": "Try saying \"Delete\". This will delete the contents of the selected row."
        },
        "note": {
            "title": "Add a Note to a Cell",
            "description": "Click a cell. Then, try saying \"Add note\". This will allow you to add a note to the focused cell."
        },
        "addNote": {
            "title": "Type the Note",
            "description": "Try saying \"Type These numbers don't match.\". This will type out your note in the cell."
        },
        "currentDate": {
            "title": "Enter Current Date",
            "description": "Click a cell. Then, try saying \"Current date\". This will enter the current date in the focused cell."
        },
        "copy": {
            "title": "Copy a Cell",
            "description": "Click the cell you just added current date to. Then, try saying \"Copy\". This will copy the current date."
        },
        "selectColumn": {
            "title": "Select a Column",
            "description": "Try saying \"Column\". This will select the entire column for the focused cell."
        },
        "paste": {
            "title": "Paste Current Date",
            "description": "Try saying \"Paste\". This will paste the current date into the selected column."
        },
        "quickActions": {
            "title": "Use Quick Actions",
            "description": "You can also do many of these controls and more with Quick Action buttons. You can customize these buttons to fit your workflow and make it even faster to review and edit spreadsheets."
        },
        "complete": {
            "description": "You now know how to use voice commands and quick actions to review and edit large spreadsheets quickly. Try exploring more commands or customizing your own quick actions for faster spreadsheet management."
        }
    },
    "socialMediaWriting": {
        "tourLabel": "Iterate on Content and Research with Private, On-Device AI",
        "learn": "Learn how to use Intelligent Generate and Rewrite, completely private and on-device, to create and iterate faster on content.",
        "onDeviceIntro": {
            "title": "Your Research Stays Private and Secure",
            "description": "Cephable's Intelligent Tasks are powered by on-device AI, meaning your data stays private and secure. You can create, rewrite, and translate text without any cloud processing. You can also use cross-application context, and iterate on ideas directly in your preferred app."
        },
        "enterPrompt": {
            "title": "Start Ideating",
            "description": "In the input box, write a prompt for content you want to scaffold or generate. For example, you can input: \"List risks of Agentic AI, where some of the gaps are, and how innovation can get ahead.\""
        },
        "customizeSettings": {
            "title": "Customize Generation Settings",
            "description": "You can customize the generation settings to fit your preferences. Adjust with system prompts that reflect tone, brand voice, and more."
        },
        "rewriteIntro": {
            "title": "Iterate on Content",
            "description": "You can use Cephable Intelligent Rewrite to iterate on content directly in your preferred app. This is all done on-device, meaning your data stays private and secure."
        },
        "highlightText": {
            "title": "Highlight Text to Rewrite",
            "description": "Highlight one of the listed risks from the generated content you want to further iterate on."
        },
        "enterInstructions": {
            "title": "Enter Rewrite Instructions",
            "description": "Type your rewrite instructions. For example, you can input: \"Add more details about the risks.\""
        },
        "writeSocialMediaPost": {
            "title": "Write a Social Media Post",
            "description": "Use rewrite again to reformat this language into a public facing social media post that obtains the key points, and highlights areas of engagement."
        },
        "highlightPost": {
            "title": "Highlight the Generated Post",
            "description": "Highlight the generated social media post you want to use."
        },
        "enterPostInstructions": {
            "title": "Enter Post Instructions",
            "description": "Type your post instructions. For example, you can input: \"Make it concise and engaging for LinkedIn.\""
        },
        "complete": {
            "description": "Now you can open your preferred social media platform, focus on the post creation text box, and paste the generated post. Or continue to iterate and refine until you are ready!"
        }
    },
    "inboxTriage": {
        "tourLabel": "AI Email Assistant: Automate, Draft & Reply Faster with Voice + AI",
        "learn": "Learn how to use voice commands and Intelligent Tasks to manage your inbox efficiently while keeping your data secure.",
        "tryOpen": {
            "title": "Try saying `Open Outlook` or `Open Gmail`",
            "description": "Make sure your microphone is on and say `Open Outlook` or `Open Gmail`. This will open your chosen email app. If you have automatic control switching enabled, it will also switch to the app controls that best match the app you are using."
        },
        "select": {
            "title": "Select an Email",
            "description": "Click an email. Then, try saying \"Select email\". This will select the focused email."
        },
        "delete": {
            "title": "Delete an Email",
            "description": "Try saying \"Delete\". This will delete the selected email."
        },
        "reply": {
            "title": "Reply to an Email",
            "description": "Click an email. Then, try saying \"Reply\". This will open the reply window for the focused email."
        },
        "dictateIntro": {
            "title": "Dictate Your Reply",
            "description": "You can use dictation to type out your reply hands-free. Make sure your microphone is on and dictation mode is activated."
        },
        "dictateReply": {
            "title": "Start Replying",
            "description": "Try saying \"I am interested in learning more about your services. Please send me additional information.\" This will type out your reply in the email. Make sure you are focused on the reply text box."
        },
        "newEmail": {
            "title": "Create a New Email",
            "description": "Try saying \"New email\". This will open a new email composition window."
        },
        "customEmailTemplates": {
            "title": "Custom Email Templates",
            "description": "You can create custom email templates for common responses. This will allow you to quickly insert pre-written content into your emails using voice commands or quick actions."
        },
        "editAppControls": {
            "title": "Edit App Controls",
            "description": "To create a custom email template, click the Edit App Controls button."
        },
        "addNewControl": {
            "title": "Add New Control",
            "description": "In the App Controls editor, click the Add New Control button."
        },
        "quickAction": {
            "title": "Select Quick Action",
            "description": "Choose Quick Action as the control type."
        },
        "templateName": {
            "title": "Name Your Template",
            "description": "Give your custom email template a name, such as \"Meeting Request.\""
        },
        "eventType": {
            "title": "Select Event Type",
            "description": "In the event type section, choose an event type of \"type\"."
        },
        "typeSteps": {
            "title": "Type Template Content",
            "description": "Type out the content of your email template. For example, you can input: \"Lead Outreach \n Hello, I would like to schedule a meeting to discuss potential collaboration opportunities. Please let me know your availability.\""
        },
        "finish": {
            "title": "Finish Creating Template",
            "description": "Click the Finish button to complete the creation of your custom email template."
        },
        "saveTemplate": {
            "title": "Save Your Template",
            "description": "Click the Save button to save your custom email template."
        },
        "focusApp": {
            "title": "Refocus on Your Email App",
            "description": "Go back to your email app and make sure you are focused on the new email subject line."
        },
        "useTemplate": {
            "title": "Use Your Template",
            "description": "Now you can use your custom email template by saying its name or clicking the Quick Action button."
        },
        "generateIntro": {
            "title": "Generate Email Content",
            "description": "You can also use Cephable Intelligent Generate to create email content directly in your email app. This is all done on-device, meaning your data stays private and secure."
        },
        "enterPrompt": {
            "title": "Enter Email Prompt",
            "description": "In the prompt box, type: Write a professional email to a potential client introducing our services and requesting a meeting."
        },
        "focusNewEmail": {
            "title": "Focus on the New Email",
            "description": "Make sure you are focused on the new email composition window where you want the generated text to appear."
        },
        "translateEmail": {
            "title": "Translate Email Content",
            "description": "You can use Cephable Intelligent Translate to translate email content directly in your email app. This is all done on-device, meaning your data stays private and secure."
        },
        "highlightText": {
            "title": "Highlight Email Content",
            "description": "Highlight the content of a long email you want to translate."
        },
        "enterLanguage": {
            "title": "Enter Translation Language",
            "description": "Type your translation language. For example, you can input: \"Spanish.\""
        },
        "complete": {
            "description": "You now know how to use voice commands and Intelligent Tasks to navigate, reply, dictate, generate, and translate emails while keeping your data secure. Try exploring more commands or customizing your own quick actions for even smoother email management."
        }
    },
    "handsFreeMeeting": {
        "tourLabel": "Reduce Meeting Fatigue with Hands-Free Controls",
        "learn": "Save time and Reduce Virtual Meeting Fatigue with Quick Hands-Free Meeting Controls",
        "tryOpen": {
            "title": "Try saying `Open Zoom` or `Open Teams`",
            "description": "Make sure your microphone is on and say `Open Zoom` or `Open Teams`. This will open your chosen meeting app. If you have automatic control switching enabled, it will also switch to the app controls that best match the app you are using."
        },
        "mute": {
            "title": "Try saying `Toggle Mute` to mute your microphone",
            "description": "Make sure your microphone is on and say `Toggle Mute`. This will mute your microphone in your chosen meeting app."
        },
        "video": {
            "title": "Try saying `Toggle Video` to turn your camera on or off",
            "description": "Make sure your microphone is on and say `Toggle Video`. This will turn your camera on or off in your chosen meeting app."
        },
        "raiseHand": {
            "title": "Try saying `Raise Hand` to raise your hand",
            "description": "Make sure your microphone is on and say `Raise Hand`. This will raise your hand in your chosen meeting app."
        },
        "quickType": {
            "title": "Quick Typing",
            "description": "Make sure your microphone is on and say `Type this is a cool feature`. This will allow you to quickly type using your voice without being in dictation mode. Make sure you are focused on a text field in your meeting app."
        },
        "complete": {
            "description": "You now know how to use voice commands and quick actions to mute/unmute, turn video on/off, raise hand, and type quickly in your favorite meeting apps. Try exploring more commands or customizing your own quick actions for even smoother meeting control."
        }
    },
    "summarizingReports": {
        "tourLabel": "Summarize Confidential Reports with Private, On-Device AI",
        "learn": "Learn how to use AI-powered summarization to quickly get the key points from long reports and documents while keeping your confidential and proprietary data secure.",
        "summarizeIntro": {
            "title": "Summarize Text",
            "description": "You can use Cephable Intelligent Tasks to quickly get the key points from long reports and documents. Summarize is part of On-device AI Tasks meaning it works without internet connection and runs from your device."
        },
        "navigateToReport": {
            "title": "Focus on your Report or Document",
            "description": "Open your report or document in your preferred app and focus on the text you want to summarize."
        },
        "highlightText": {
            "title": "Highlight Text to Summarize",
            "description": "Highlight the text you want to summarize."
        },
        "copyText": {
            "title": "Copy the Text",
            "description": "After highlighting your text, copy it to prepare it for summarization."
        },
        "enterPrompt": {
            "title": "Enter Summarization Instructions",
            "description": "Type your summarization instructions. Specify the length, style, or any specific content you want."
        },
        "pasteText": {
            "title": "Paste your Report Content",
            "description": "After the instructions, paste the copied text into the input box."
        },
        "focusChatApp": {
            "title": "Focus on your Chat App",
            "description": "Open your chat app like Teams or Slack and focus on the text box where you want the summarized text to appear."
        },
        "rewriteIntro": {
            "title": "Rewrite Summarized Text",
            "description": "You can also use Cephable Intelligent Rewrite to further refine the summarized text. This is all done on-device, meaning your data stays private and secure."
        },
        "highlightSummary": {
            "title": "Highlight the Summarized Text",
            "description": "Highlight the summarized text you want to further refine."
        },
        "enterRewriteInstructions": {
            "title": "Enter Rewrite Instructions",
            "description": "Type your rewrite instructions. Specify the tone, style, or any specific changes you want."
        },
        "complete": {
            "description": "You have learned how to use Intelligent Tasks for quick and secure text summarization to share findings with your team. Continue exploring more use cases with Summarize and Rewrite, or other features to optimize your workflow."
        }
    },
    "presentationControls": {
        "tourLabel": "Control Presentations with Voice Commands",
        "learn": "Learn how to use voice commands and quick actions to navigate and control your presentations hands-free.",
        "tryOpen": {
            "title": "Try saying `Open PowerPoint` or `Open Google Slides`"
        },
        "startPresentation": {
            "title": "Try saying `Start Presentation` to begin your slideshow",
            "description": "Make sure your microphone is on and say `Start Presentation`. This will start your slideshow in your chosen presentation app."
        },
        "nextSlide": {
            "title": "Try saying `Next Slide` to move to the next slide",
            "description": "Make sure your microphone is on and say `Next Slide`. This will move to the next slide in your slideshow."
        },
        "previousSlide": {
            "title": "Try saying `Previous Slide` to go back to the previous slide",
            "description": "Make sure your microphone is on and say `Previous Slide`. This will go back to the previous slide in your slideshow."
        },
        "escape": {
            "title": "Try saying `Escape` to exit the presentation mode",
            "description": "Make sure your microphone is on and say `Escape`. This will exit the presentation mode in your chosen presentation app."
        },
        "complete": {
            "description": "You now know how to use voice commands and quick actions to start, navigate, and exit presentations hands-free. Try exploring more commands or customizing your own quick actions for even smoother presentation control."
        }
    },
    "camera": {
        "tourLabel": "Camera Controls",
        "rowDescription": "Camera controls allow you to use your face and body to control your device. You can use different face expressions, head movements to control your device. With the microphone on you can use voice commands to start and stop the camera, and switch cameras.",
        "learn": "Learn how to use camera controls to navigate and interact with your device hands-free.",
        "welcome": {
            "title": "Camera Controls",
            "description": "Camera controls let you use your face and body to control your device. You can use different face expressions, head movements, and voice commands to navigate and interact with your device hands-free."
        },
        "start": {
            "title": "Start Camera Controls",
            "description": "To start using camera controls if you are using voice, say \"Start camera controls\" or click the Camera Controls button."
        }
    },
    "microphone": {
        "tourLabel": "Voice Controls",
        "learn": "Learn how you can use your voice to control any app and use dictation to type what you say.",
        "description": "Cephable Voice Controls allow you to use your voice to control your device. Want to save that document? Just say 'save'. Want to pause your music? Just say 'pause'. Voice also allows you to use dictation to type what you say.",
        "rowDescription": "When the microphone is off, we will not listen to your voice commands at all but if you turn it on, you will be able to use global commands to toggle pause, start, and more. Let's turn it on.",
        "start": {
            "title": "Start Voice Controls",
            "description": "To start using voice controls, click the Microphone Toggle on the Cephable Panel. This will turn on the microphone and start listening for commands."
        },
        "tryOpen": {
            "title": "Try saying `Open Spotify` or `Open Word`",
            "description": "`Open` is a global command that will open the app you specify and send focus to it. If you have automatic control switching enabled, it will also switch to the app controls that best match the app you are using."
        },
        "finishedCommands": {
            "title": "Excellent! You've learned the basics of Voice Controls.",
            "description": "You can always revisit the tour from the Support Page. The next step is to learn about Dictation. You can start the tour from the support page."
        }
    },
    "publicProfiles": {
        "tourLabel": "Customize App Controls"
    },
    "editingImages": {
        "tourLabel": "Faster Image Editing with Voice Controls and Dictation",
        "learn": "Learn how to use voice commands, quick actions, and dictation to improve your image editing process.",
        "tryOpen": {
            "title": "Try saying `Open Figma` or `Open Photoshop`"
        },
        "selectAll": {
            "title": "Try saying `Select All` to select all elements on the canvas",
            "description": "Make sure your microphone is on and say `Select All`. This will select all elements on the canvas in your chosen image editing app. Make sure you are focused on the app."
        },
        "bold": {
            "title": "Try saying `Bold` to make the selected text bold",
            "description": "Make sure your microphone is on and say `Bold`. This will make the selected text bold in your chosen image editing app. Make sure you have text selected."
        },
        "copy": {
            "title": "Try saying `Copy` to copy the selected element",
            "description": "Make sure your microphone is on and say `Copy`. This will copy the selected element in your chosen image editing app. Make sure you have an element selected."
        },
        "paste": {
            "title": "Try saying `Paste` to paste the copied element",
            "description": "Make sure your microphone is on and say `Paste`. This will paste the copied element in your chosen image editing app."
        },
        "delete": {
            "title": "Try saying `Delete` to delete the selected element",
            "description": "Make sure your microphone is on and say `Delete`. This will delete the selected element in your chosen image editing app. Make sure you have an element selected."
        },
        "dictationIntro": {
            "title": "Dictation",
            "description": "You can use dictation to type text using your voice. Make sure your microphone is on and dictation mode is activated."
        },
        "focus": {
            "title": "Focus on a Text Field",
            "description": "Click on a text field in your image editing app where you want to use dictation."
        },
        "speaking": {
            "title": "Start Speaking",
            "description": "Try saying \"For early discount codes, scan the QR Code. This is valid until September 30th. Then, apply through the website.\" Make sure you are focused on the text field or area where you want to dictate."
        },
        "complete": {
            "description": "You now know how to use voice commands, quick actions, and dictation to improve your image editing process. Try exploring more commands or customizing your own quick actions for even smoother image editing."
        }
    },
    "smartTasks": {
        "tourLabel": "Intelligent Tasks",
        "rowDescription": "Intelligent tasks are the different tasks that Cephable's AI can perform for you. These tasks are powered by on-device AI and can be used to create text, summarize, rewrite, translate, and more.",
        "learn": "Learn how to generate, rewrite, and translate text with Cephable.",
        "generate": {
            "openTool": {
                "title": "Open Generate Text Tool",
                "description": "In the Cephable panel, click the carrot next to Intelligent Tasks and select Generate Text."
            },
            "focusApp": {
                "title": "Focus on the App",
                "description": "Make sure you are focused on the app where you want the generated text to appear. You will be able to seamlessly generate text into that location when you click Generate."
            },
            "enterPrompt": {
                "title": "Enter Prompt",
                "description": "In the prompt box, type: Create an ink-credible one sentence pun or a short poem about octopuses."
            },
            "clickGenerate": {
                "title": "Click Generate",
                "description": "Click the Generate button. Watch the text appear in real time."
            }
        },
        "rewrite": {
            "openTool": {
                "title": "Open Rewrite Text Tool",
                "description": "In the Cephable panel, click the carrot next to Intelligent Tasks and select Rewrite Text."
            },
            "focusApp": {
                "title": "Focus on the App",
                "description": "Make sure you are focused on the app where you want the rewritten text to appear. You will be able to seamlessly generate text into that location when you click Generate."
            },
            "welcome": {
                "title": "Welcome to Rewrite",
                "description": "Rephrase content quickly with highlighted or pasted text."
            },
            "highlightToggle": {
                "title": "Use Highlighted Text",
                "description": "Ensure 'Use Highlighted Text' is selected (default)."
            },
            "highlightText": {
                "title": "Highlight Text to Rewrite",
                "description": "Highlight: An octopus can control each arm independently."
            },
            "enterInstructions": {
                "title": "Enter Rewrite Instructions",
                "description": "Type: In an old English style."
            },
            "clickGenerate": {
                "title": "Click Generate",
                "description": "Your highlighted text will be rewritten based on your input."
            },
            "pasteMode": {
                "title": "Use Paste Text",
                "description": "Paste original and rewrite instruction. Click Generate. Click Clear to reset."
            }
        },
        "translate": {
            "welcome": {
                "title": "Welcome to Translate",
                "description": "Translate highlighted or pasted text into another language."
            },
            "openTool": {
                "title": "Open Translate Text Tool",
                "description": "In the Cephable panel, click the carrot next to Intelligent Tasks and select Translate Text."
            },
            "highlightToggle": {
                "title": "Use Highlighted Text",
                "description": "Make sure 'Use Highlighted Text' is selected."
            },
            "highlightText": {
                "title": "Highlight Text to Translate",
                "description": "Highlight: An octopus can control each arm independently."
            },
            "enterLanguage": {
                "title": "Enter Target Language",
                "description": "Type: Portuguese."
            },
            "clickGenerate": {
                "title": "Click Generate",
                "description": "Translation will be typed into your app at your cursor."
            },
            "pasteMode": {
                "title": "Use Paste Text",
                "description": "Paste text and language. Click Generate. Click Clear to reset."
            }
        }
    },
    "featureFlag": {
        "title": "Time to Upgrade",
        "description": "The feature you are trying to use requires a different license. Upgrade to unlock this feature and more. "
    },
    "tour": {
        "title": "Welcome to Cephable",
        "description": "Cephable is a powerful tool that allows you to control your computer using your voice, camera, and other inputs. This tour will guide you through the key features and how to use them effectively.",
        "startTour": "Start Tour",
        "finishTour": "Finish Tour",
        "statusDialog": "Please check the panel for more information.",
        "step": "Step {{step}} of {{total}}",
        "next": "Next",
        "previous": "Previous",
        "endTour": "End Tour",
        "nextTourLabel": "Next: {{nextStepTitle}}",
        "skipTour": "Skip Tour",
        "steps": {
            "welcome": {
                "title": "Welcome to Cephable",
                "description": "This tour will guide you through the main features of Cephable. Let's get started!"
            },
            "inputs": {
                "title": "Inputs Overview",
                "description": "Cephable supports various input methods including voice commands, camera controls, and more. You can customize these inputs in the settings."
            },
            "appControls": {
                "title": "App Controls",
                "description": "Create app controls to map your inputs to specific actions in your applications. This allows you to control apps with gestures, voice commands, and more."
            },
            "settings": {
                "title": "Settings",
                "description": "Access the settings to configure your inputs, app controls, and other preferences. You can also manage your account and language settings here."
            },
            "intelligentTasks": {
                "title": "Intelligent Tasks",
                "description": "Use intelligent tasks to generate text, summarize content, rewrite text, translate languages, and more using AI-powered features."
            },
            "cameraControls": {
                "title": "Camera Controls",
                "description": "Enable camera controls to use facial expressions and head movements as input methods for controlling your computer."
            },
            "voiceControls": {
                "title": "Voice Controls",
                "description": "Use voice commands to control your computer hands-free. You can dictate text, issue commands, and interact with applications using your voice."
            }
        }
    },
    "support": {
        "tourLabel": "Ready to Learn More?",
        "description": "You can visit our support hub for more information on how to use Cephable, including more tours, step-by-step guides, tutorials, and videos.",
        "cta": "Explore Other Tours"
    },
    "onboarding": {
        "demoPlaceholder": "This is the placeholder demo text. Using the prompts above you can control the styling of this text.",
        "exit": "Exit Setup",
        "overview": "Overview",
        "learn": "Learn about the features of Cephable and how to use them.",
        "start": "Looks like you are just getting started. Here are some areas that will help you along the way.",
        "startTour": " Get Started",
        "dashboard": {
            "title": "Cephable Dashboard",
            "description": "The Cephable Dashboard is your central hub for managing your device, app controls, and settings. From here, you can customize your experience, view active commands, and access all the features of Cephable."
        },
        "step1": {
            "title": "Welcome to Cephable",
            "description": "Cephable is your personal AI — built for fast actions, voice commands, content generation, and smart automation across your tools. Out-of-the-box controls. No cloud processing. Just productivity that moves with you.",
            "inputs": "What are Inputs?",
            "inputDescription": "Input controls are the methods for operating your device, extending your computer capabilities beyond the traditional mouse and keyboard. These methods include voice commands, head movements, facial expressions, and additional peripherals.",
            "profiles": "What are App Controls?",
            "profileDescription": "App Controls allow you to connect and communicate with your computer in unique ways by pairing input and output controls. With app controls, you can instruct Cephable to perform tasks like saving a file with a head tilt or clicking 'send' on an email with a smile.",
            "action": "Explore Input Controls",
            "secondaryAction": "Skip to App Controls",
            "secondaryActionDescription": "Already know about input controls?"
        },
        "finish": {
            "title": "Setup Complete",
            "description": "You have successfully used your first Cephable app controls! You can now utilize these and more on your device. It is available on your Cephable Dashboard where you can continue to customize app controls settings as needed.",
            "continueToApp": "Continue to Cephable Dashboard",
            "keepExploring": "Keep Exploring"
        },
        "useCaseSlider": {
            "context": "I'd like to use",
            "control": "to control"
        }
    },
    "panel": {
        "description": "This is the panel which is the main control center. It is always visible and allows you to control your apps using your voice, and more. Let's start with the microphone."
    }
}
